import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityBodyInterface,
  ActivityInterface,
  ActivityTypeEnum,
  FileTypeEnum,
  OfferInterface,
} from '@sport-activities/sdk';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';
import slugify from 'slugify';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';

import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { base64toBlob } from '~/core/helpers/image/base64-to-blob/base64-to-blob.helper';
import { getExtensionFromBase64 } from '~/core/helpers/image/get-extension-from-base64/get-extension-from-base64.helper';
import { generateUid } from '~/core/helpers/uid/uid.helper';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import {
  getRatio,
  mediaResolutions,
} from '~/organizer/shared/helpers/media/media.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

interface SportOfferInterface {
  assetIdentifier: string;
  description: string;
  identifier: number;
  image: string;
  name: string;
}

@Component({
  selector: 'app-organizer-activity-creation-birthday-sport-offer',
  templateUrl: './birthday-sport-offer.component.html',
})
export class OrganizerActivityCreationPageSportBirthdayOfferComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  protected readonly pageTitleKey = '';

  public readonly tipsVariant = TipsVariantEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;

  private offerIndex = 0;

  public isLoading$ = new BehaviorSubject(false);
  public offers: OfferInterface[];
  public sportsList: SportOfferInterface[] = [];
  public activityType: ActivityTypeEnum;

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly router: Router,
    private readonly translateService: TranslateService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get imageAspectRatio(): number {
    return getRatio(ActivityMediaResolutionEnum.LARGE);
  }

  public get importedAndCheckedSports(): {
    checked: boolean;
    sportOffer: SportOfferInterface;
  }[] {
    return this.importedSports.value.filter(
      (s: { checked: boolean; sportOffer: SportOfferInterface }) => s.checked
    );
  }

  // sports imported from previous offer
  public get importedSports(): FormArray {
    return this.formGroup.get('importedSports') as FormArray;
  }

  public get isFirstOffer(): boolean {
    return this.offerIndex === 0;
  }

  // number of sport a customer can choose in this offer
  public get maximumSportSelectable(): FormControl {
    return this.formGroup.get('maximumSportSelectable') as FormControl;
  }

  // current offer name
  public get offerName(): string {
    return this.offers[this.offerIndex].name;
  }

  // nav  params
  get previousNavigationExtras(): NavigationExtras {
    if (this.offerIndex <= 0) {
      return {};
    }

    return {
      queryParams: {
        offerIndex: this.offerIndex - 1,
      },
    };
  }

  // previous offer title
  public get previousOfferTitle(): string {
    return this.offerIndex > 0 ? this.offers[this.offerIndex - 1].name : null;
  }

  // previous page url
  public get previousUrl(): string {
    return this.offerIndex > 0
      ? this.navigationService.getCurrentPath(this.activatedRoute)
      : null;
  }

  // custom offer sports
  public get sports(): FormArray {
    return this.formGroup.get('sports') as FormArray;
  }

  // counter max value
  public get totalSportsNumber(): number {
    const sportsCount = this.sports?.length || 0;
    const importCount = this.importedAndCheckedSports?.length || 0;
    return importCount + sportsCount;
  }

  // process update case
  private handleUpdate(offer: OfferInterface, currentOfferIndex: number): void {
    const maximumSportSelectable = +offer.meta?.maximumSportSelectable;
    const sports: SportOfferInterface[] = offer.meta
      ?.sports as unknown as SportOfferInterface[];

    // set max value
    this.maximumSportSelectable.setValue(maximumSportSelectable);
    const activity = this.activityService.activity;

    // first offer don't have any importedSports because it is the first page
    if (currentOfferIndex === 0) {
      return sports.forEach((sport) => {
        sport.image = activity.getMediaUrlByIdentifier(
          sport.assetIdentifier,
          ActivityMediaResolutionEnum.LARGE
        );
        this.addSport(sport);
      });
    }

    // create form group for imported sports
    this.sportsList.forEach((sportOffer) => {
      this.importedSports.push(
        new FormGroup({
          sportOffer: new FormControl(sportOffer),
          checked: new FormControl(),
        })
      );
    });

    // for every sports
    sports.forEach((sport) => {
      let isImportedSport = false;

      this.importedSports.controls.forEach((importedSport) => {
        if (
          sport.identifier === importedSport.get('sportOffer').value.identifier
        ) {
          importedSport.get('checked').setValue(true);
          isImportedSport = true;
        }
      });

      if (!isImportedSport) {
        sport.image = activity.getMediaUrlByIdentifier(
          sport.assetIdentifier,
          ActivityMediaResolutionEnum.LARGE
        );
        this.addSport(sport);
      }
    });

    this.maximumSportSelectable.updateValueAndValidity();
    this.sports.updateValueAndValidity();
    this.importedSports.updateValueAndValidity();
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      indicativeOffers: this.offers.map((offer) => {
        return {
          ...offer,
          price: +offer.price,
          name: offer.name,
          description: offer.description,
          meta: offer.meta,
        };
      }),
    };
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    return {
      indicativeOffers: this.offers.map((offer) => {
        return {
          ...offer,
          price: +offer.price,
          name: [this.locatableService.buildCurrentLocatableBody(offer.name)],
          description: [
            this.locatableService.buildCurrentLocatableBody(offer.description),
          ],
          meta: offer.meta,
        };
      }),
    };
  }

  // form validation
  protected buildValidationConfig(): void {
    this.validationConfig = {
      maximumSportSelectable: new FormControl(0, [
        Validators.required,
        Validators.min(1),
        (control: AbstractControl) =>
          Validators.max(this.formGroup ? this.totalSportsNumber : 1)(control), // dynamic value
      ]),
      sports: new FormArray([]),
      importedSports: new FormArray([]),
    };
  }

  protected onInitHook(): void {
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      const { offerIndex = 0 } = queryParams;
      this.offerIndex = +offerIndex;
      this.offers = [...this.activityService.activity.indicativeOffers];
      this.activityType = this.activityService.activity.type;

      const currentOffer = this.offers[offerIndex];

      this.resetForm();

      // we are in update case
      // remove this and the method when update will be handle in another page as it should be
      if (currentOffer?.meta) {
        return this.handleUpdate(currentOffer, offerIndex);
      }

      if (offerIndex > 0) {
        // load already saved sports
        return this.sportsList.forEach((sportOffer) => {
          this.importedSports.push(
            new FormGroup({
              sportOffer: new FormControl(sportOffer),
              checked: new FormControl(true),
            })
          );
        });
      }
      // first offer = we display a default sport form
      this.addSport();
    });
  }

  // add a sport row to the form
  public addSport(sport?: SportOfferInterface): void {
    this.sports.push(
      new FormGroup({
        identifier: new FormControl(sport?.identifier || generateUid()),
        name: new FormControl(sport?.name, Validators.required),
        description: new FormControl(sport?.description, Validators.required),
        image: new FormControl(sport?.image, Validators.required),
      })
    );
  }

  public getErrors(name: string, index: number): ValidationErrors {
    const control = this.sports.at(index).get(name);

    return control?.touched && control?.errors;
  }

  public removeSport(index: number): void {
    this.sports.removeAt(index);
    this.maximumSportSelectable.updateValueAndValidity();
  }

  public resetForm(): void {
    this.sports.clear();
    this.importedSports.clear();
    this.maximumSportSelectable.setValue(0);
    this.isLoading$.next(false);
  }

  public saveSportOffer(formGroupValue: FormGroupValueInterface): void {
    const importedSports = formGroupValue.importedSports
      .filter((importedSport) => importedSport.checked)
      .map((importedSport) => importedSport.sportOffer);

    // Build data //
    this.offers[this.offerIndex].meta = {
      maximumSportSelectable: formGroupValue.maximumSportSelectable,
      sports: formGroupValue.sports?.concat(importedSports),
    };

    this.sportsList = this.sportsList?.concat(formGroupValue.sports);

    // When all offers are updated, go to next step //
    if (!this.offers[this.offerIndex + 1]) {
      of(this.offers)
        .pipe(
          mergeMap((offers) => offers),
          mergeMap((offer) => {
            const sports =
              (offer.meta.sports as unknown as SportOfferInterface[]) || [];

            return forkJoin(
              sports.map((sport) => {
                // update case: nothing updated
                if (sport.assetIdentifier) {
                  return of(undefined);
                }
                // update case : url will not be a base64 in case of an existing picture
                // so we just skip ip
                if (sport.image?.startsWith('http')) {
                  sport.assetIdentifier =
                    this.activityService.activity.medias.find(
                      (m) => m.url === sport.image
                    ).identifier;
                  sport.image = undefined; // remove image base64 from meta
                  return of(undefined);
                }
                const extension = getExtensionFromBase64(sport.image);
                // unique id on name is usefull because CDN has a cache that make strange behavior on update use case
                // when the image has already been cached
                const filename = `${generateUid()}_${slugify(
                  sport.name
                )}.${extension}`;

                return this.activityService
                  .addMedia(
                    this.activityService.activity.sku,
                    base64toBlob(sport.image),
                    filename,
                    ['SECONDARY_MEDIA' as FileTypeEnum],
                    [mediaResolutions[ActivityMediaResolutionEnum.LARGE]]
                  )
                  .pipe(
                    map((uploadResponse) => {
                      // update asset identifier
                      sport.assetIdentifier = uploadResponse.identifier;
                      sport.image = undefined; // remove image base64 from meta
                      return uploadResponse;
                    })
                  );
              })
            );
          }),
          toArray()
        )
        .subscribe(() => this.submit(this.offers));

      return;
    }

    // Redirect to the next formula //
    const path = this.navigationService.getCurrentPath(this.activatedRoute, {
      queryParams: {
        offerIndex: this.offerIndex + 1,
      },
      queryParamsHandling: 'merge',
    });

    this.router.navigateByUrl(path);
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    super.submit(formGroupValue);
  }

  public updateUpload(sportIndex: number): void {
    const control = this.sports.controls[sportIndex];
    const sport: SportOfferInterface = control.value;

    let delete$: Observable<void> = of(undefined);
    if (sport.image?.startsWith('http')) {
      const mediaToDelete = this.activityService.activity.medias.find(
        (m) => m.url === sport.image
      );
      delete$ = this.activityService.deleteMedia(
        this.activityService.activity.sku,
        mediaToDelete.identifier
      );
    }

    delete$.subscribe(() => {
      control.patchValue({
        ...sport,
        image: null,
      });
    });
  }

  public translateKey(key: string, interpolateParams?: object): string {
    const baseKey = `organizer.creation.${this.activityType?.toLowerCase()}.sport_offer.`;

    return this.translateService.instant(`${baseKey}${key}`, interpolateParams);
  }
}
